import CatalogCrud, { TextField, ArrayEditor, ActiveToggleRow, VioletSwitch } from "./CatalogCrud";

const BLANK = {
  name: "",
  projectPath: "",
  configuration: "Release",
  publishDirectory: ".artifacts\\publish",
  buildRunner: "MSBuild",
  msBuildPath: "msbuild",
  additionalArguments: [],
  frontendBuildOutputDirectory: "build",
  installFrontendDependencies: true,
  preserveTargetRootWebConfig: true,
  preserveFilePatterns: [],
  migrationProvider: "EF6",
  migrationConnectionStringName: "CertificadosContext",
  appSettingsFileNames: ["appsettings.json", "appsettings.Production.json"],
  ef6ToolPath: "",
  ef6AssemblyPath: "",
  ef6ProjectDirectory: "",
  ef6MigrationsConfigurationType: "",
  efCoreProjectPath: "",
  efCoreStartupProjectPath: "",
  efCoreDbContext: "",
  efCoreAdditionalArguments: [],
  isActive: true,
};

function snapshotFrom(item) {
  return {
    name: item.name ?? "",
    projectPath: item.projectPath ?? "",
    configuration: item.configuration ?? "Release",
    publishDirectory: item.publishDirectory ?? "",
    buildRunner: item.buildRunner ?? "MSBuild",
    msBuildPath: item.msBuildPath ?? "",
    additionalArguments: Array.isArray(item.additionalArguments) ? [...item.additionalArguments] : [],
    frontendBuildOutputDirectory: item.frontendBuildOutputDirectory ?? "build",
    installFrontendDependencies: item.installFrontendDependencies !== false,
    preserveTargetRootWebConfig: item.preserveTargetRootWebConfig !== false,
    preserveFilePatterns: Array.isArray(item.preserveFilePatterns) ? [...item.preserveFilePatterns] : [],
    migrationProvider: item.migrationProvider ?? "EF6",
    migrationConnectionStringName: item.migrationConnectionStringName ?? "CertificadosContext",
    appSettingsFileNames: Array.isArray(item.appSettingsFileNames)
      ? [...item.appSettingsFileNames]
      : ["appsettings.json", "appsettings.Production.json"],
    ef6ToolPath: item.ef6ToolPath ?? "",
    ef6AssemblyPath: item.ef6AssemblyPath ?? "",
    ef6ProjectDirectory: item.ef6ProjectDirectory ?? "",
    ef6MigrationsConfigurationType: item.ef6MigrationsConfigurationType ?? "",
    efCoreProjectPath: item.efCoreProjectPath ?? "",
    efCoreStartupProjectPath: item.efCoreStartupProjectPath ?? "",
    efCoreDbContext: item.efCoreDbContext ?? "",
    efCoreAdditionalArguments: Array.isArray(item.efCoreAdditionalArguments) ? [...item.efCoreAdditionalArguments] : [],
    isActive: !!item.isActive,
  };
}

function validate(snap) {
  const isFrontendRunner = snap.buildRunner === "NpmBuild" || snap.buildRunner === "YarnBuild";
  if (!snap.name.trim()) return "Informe o nome.";
  if (!snap.projectPath.trim()) return isFrontendRunner ? "Informe a pasta do frontend ou package.json." : "Informe o caminho do projeto.";
  if (!snap.configuration.trim()) return "Informe a configuração (Debug/Release/etc).";
  if (!snap.publishDirectory.trim()) return "Informe o diretório de publicação.";
  if (snap.buildRunner === "MSBuild" && !snap.msBuildPath.trim()) return "Informe o caminho do MSBuild.";
  if (isFrontendRunner && !snap.frontendBuildOutputDirectory.trim()) return "Informe a pasta de saída do build frontend.";
  if (isFrontendRunner && snap.migrationProvider !== "None") return "Para build frontend, use Migration provider None.";
  if (snap.migrationProvider === "EFCore") {
    if (!snap.migrationConnectionStringName.trim()) return "Informe o nome da connection string.";
    if (!snap.efCoreProjectPath.trim()) return "Informe o projeto de migrations EF Core.";
    if (!snap.efCoreStartupProjectPath.trim()) return "Informe o projeto startup EF Core.";
    if (!snap.efCoreDbContext.trim()) return "Informe o DbContext EF Core.";
  }
  return null;
}

function SelectField({ label, value, onChange, disabled, options, helper }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
        {label}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="w-full rounded-xl border border-slate-200 bg-slate-50/50 px-3 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent focus:bg-white transition disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {helper && <p className="mt-1 text-[11px] text-slate-500">{helper}</p>}
    </div>
  );
}

export default function BuildConfigList() {
  return (
    <CatalogCrud
      title="Build Configurations"
      description="Perfis de build usados para compilar e empacotar a aplicação antes do deploy."
      endpoint="/admin/build-configurations"
      duplicateEnabled
      duplicateSuccessMsg="Configuração de build duplicada com sucesso"
      itemSubtitle={(item) => item.projectPath || item.configuration || null}
      blank={BLANK}
      snapshotFrom={snapshotFrom}
      validate={validate}
      renderForm={({ form, update, saving }) => (
        <>
          <TextField
            label="Nome"
            value={form.name}
            onChange={(v) => update("name", v)}
            placeholder="Ex: Qualitycert Web Debug"
            disabled={saving}
            required
          />

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <TextField
                label="Project path"
                value={form.projectPath}
                onChange={(v) => update("projectPath", v)}
                placeholder={form.buildRunner === "NpmBuild" || form.buildRunner === "YarnBuild" ? "frontend\\package.json" : "MyProject\\MyProject.csproj"}
                disabled={saving}
                required
                mono
                helper={form.buildRunner === "NpmBuild" || form.buildRunner === "YarnBuild" ? "Informe o package.json ou a pasta que contém o package.json." : null}
              />
            </div>
            <TextField
              label="Configuration"
              value={form.configuration}
              onChange={(v) => update("configuration", v)}
              placeholder="Release"
              disabled={saving}
              required
            />
            <TextField
              label="Publish directory"
              value={form.publishDirectory}
              onChange={(v) => update("publishDirectory", v)}
              placeholder=".artifacts\\publish"
              disabled={saving}
              required
              mono
            />
            <SelectField
              label="Build runner"
              value={form.buildRunner}
              onChange={(v) => update("buildRunner", v)}
              disabled={saving}
              options={[
                { value: "MSBuild", label: "MSBuild" },
                { value: "DotnetPublish", label: "dotnet publish" },
                { value: "NpmBuild", label: "npm build" },
                { value: "YarnBuild", label: "yarn build" },
              ]}
            />
            {form.buildRunner === "MSBuild" && (
              <TextField
                label="MSBuild path"
                value={form.msBuildPath}
                onChange={(v) => update("msBuildPath", v)}
                placeholder="C:\\Program Files\\…\\MSBuild.exe"
                disabled={saving}
                required={form.buildRunner !== "DotnetPublish"}
                mono
              />
            )}
            {(form.buildRunner === "NpmBuild" || form.buildRunner === "YarnBuild") && (
              <>
                <TextField
                  label="Frontend output"
                  value={form.frontendBuildOutputDirectory}
                  onChange={(v) => update("frontendBuildOutputDirectory", v)}
                  placeholder="build"
                  disabled={saving}
                  required
                  mono
                  helper="Use build para Create React App ou dist para Vite."
                />
                <label className={`flex items-start justify-between gap-4 p-4 rounded-xl ring-1 transition cursor-pointer ${
                  form.installFrontendDependencies ? "bg-violet-50/60 ring-violet-200" : "bg-slate-50/50 ring-slate-200"
                }`}>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-800">Instalar dependências</p>
                    <p className="text-xs text-slate-500 mt-0.5">Roda npm ci/install ou yarn install antes do build.</p>
                  </div>
                  <VioletSwitch
                    checked={form.installFrontendDependencies}
                    onChange={(v) => update("installFrontendDependencies", v)}
                    disabled={saving}
                  />
                </label>
              </>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
              Argumentos adicionais
            </label>
            <ArrayEditor
              items={form.additionalArguments}
              onChange={(v) => update("additionalArguments", v)}
              placeholder="/p:VisualStudioVersion=17.0"
              disabled={saving}
            />
            <p className="mt-1 text-[11px] text-slate-500">Parâmetros extras passados para o runner selecionado.</p>
          </div>

          <div>
            <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
              Arquivos preservados/excluídos da cópia
            </label>
            <ArrayEditor
              items={form.preserveFilePatterns}
              onChange={(v) => update("preserveFilePatterns", v)}
              placeholder="appsettings*.json"
              disabled={saving}
            />
            <p className="mt-1 text-[11px] text-slate-500">Padrões enviados ao robocopy /XF, além do web.config quando a opção abaixo estiver ativa.</p>
          </div>

          <label className={`flex items-start justify-between gap-4 p-4 rounded-xl ring-1 transition cursor-pointer ${
            form.preserveTargetRootWebConfig ? "bg-violet-50/60 ring-violet-200" : "bg-slate-50/50 ring-slate-200"
          }`}>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-800">Preservar web.config raiz do destino</p>
              <p className="text-xs text-slate-500 mt-0.5">Mantém o web.config atual do destino durante o deploy.</p>
            </div>
            <VioletSwitch
              checked={form.preserveTargetRootWebConfig}
              onChange={(v) => update("preserveTargetRootWebConfig", v)}
              disabled={saving}
            />
          </label>

          <div className="pt-3 border-t border-slate-100 space-y-4">
            <h3 className="text-sm font-bold text-slate-800">Migrations</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <SelectField
                label="Migration provider"
                value={form.migrationProvider}
                onChange={(v) => update("migrationProvider", v)}
                disabled={saving}
                options={[
                  { value: "EF6", label: "EF6" },
                  { value: "EFCore", label: "EF Core" },
                  { value: "None", label: "None" },
                ]}
              />
              <TextField
                label="Connection string name"
                value={form.migrationConnectionStringName}
                onChange={(v) => update("migrationConnectionStringName", v)}
                placeholder="DefaultConnection"
                disabled={saving}
              />
            </div>

            {form.migrationProvider === "EFCore" && (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <TextField
                    label="EF Core project"
                    value={form.efCoreProjectPath}
                    onChange={(v) => update("efCoreProjectPath", v)}
                    placeholder="Precavida.Infrastructure\\Precavida.Infrastructure.csproj"
                    disabled={saving}
                    required
                    mono
                  />
                  <TextField
                    label="EF Core startup project"
                    value={form.efCoreStartupProjectPath}
                    onChange={(v) => update("efCoreStartupProjectPath", v)}
                    placeholder="Precavida.API\\Precavida.API.csproj"
                    disabled={saving}
                    required
                    mono
                  />
                  <div className="sm:col-span-2">
                    <TextField
                      label="DbContext"
                      value={form.efCoreDbContext}
                      onChange={(v) => update("efCoreDbContext", v)}
                      placeholder="ApplicationDbContext"
                      disabled={saving}
                      required
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                    Appsettings para leitura da connection string
                  </label>
                  <ArrayEditor
                    items={form.appSettingsFileNames}
                    onChange={(v) => update("appSettingsFileNames", v)}
                    placeholder="appsettings.Production.json"
                    disabled={saving}
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-1.5">
                    Argumentos adicionais EF Core
                  </label>
                  <ArrayEditor
                    items={form.efCoreAdditionalArguments}
                    onChange={(v) => update("efCoreAdditionalArguments", v)}
                    placeholder="--verbose"
                    disabled={saving}
                  />
                </div>
              </>
            )}

            {form.migrationProvider === "EF6" && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <TextField
                  label="EF6 tool path"
                  value={form.ef6ToolPath}
                  onChange={(v) => update("ef6ToolPath", v)}
                  placeholder="packages\\EntityFramework.6.4.4\\tools\\net45\\any\\ef6.exe"
                  disabled={saving}
                  mono
                />
                <TextField
                  label="EF6 assembly path"
                  value={form.ef6AssemblyPath}
                  onChange={(v) => update("ef6AssemblyPath", v)}
                  placeholder="Project.Entity\\bin\\Debug\\Project.Entity.dll"
                  disabled={saving}
                  mono
                />
                <TextField
                  label="EF6 project directory"
                  value={form.ef6ProjectDirectory}
                  onChange={(v) => update("ef6ProjectDirectory", v)}
                  placeholder="Project.Entity"
                  disabled={saving}
                  mono
                />
                <div className="sm:col-span-2">
                  <TextField
                    label="EF6 migrations configuration type"
                    value={form.ef6MigrationsConfigurationType}
                    onChange={(v) => update("ef6MigrationsConfigurationType", v)}
                    placeholder="Project.Entity.Migrations.Configuration"
                    disabled={saving}
                    mono
                  />
                </div>
                <p className="sm:col-span-2 text-[11px] text-slate-500">Se estes campos ficarem vazios, o deployer usa os valores EF6 legados cadastrados na máquina.</p>
              </div>
            )}
          </div>

          <ActiveToggleRow
            checked={form.isActive}
            onChange={(v) => update("isActive", v)}
            disabled={saving}
            label="Configuração ativa"
            description="Desative para ocultar esta configuração da seleção em novos ambientes."
          />
        </>
      )}
    />
  );
}
